// 大文件的本机持久缓存（Cache Storage）：3D 场景 / 模型 / 皮肤贴图、回放回合数据。
// 这些文件的地址都带版本或内容哈希（回合文件的 key 每次解析都换），内容永不改变，下载一次后长期留在本机，再次打开不走网络；
// 浏览器 HTTP 缓存空间紧张时会被挤掉，预签名地址每次都变也命中不了，所以单独存。缓存不可用（非安全上下文、隐私模式）时直接走网络。
// 另有后台预热队列（空闲时下载、限制并发、省流量模式下不预热）与开发用的慢网模拟（见 devNet）。

/** static：版本化的静态资源（换版本时按版本号清理）；replay：回合数据（只保留最近若干个，退出登录时清空）。 */
export type CacheBucket = "static" | "replay";

const NAMES: Record<CacheBucket, string> = { static: "cs2coach-static-v1", replay: "cs2coach-replay-v1" };
/** 回合文件最多保留多少个（单个几十 KB 到几百 KB）。 */
const REPLAY_MAX = 800;

const opened = new Map<CacheBucket, Promise<Cache | null>>();

function open(bucket: CacheBucket): Promise<Cache | null> {
  let p = opened.get(bucket);
  if (!p) {
    p = typeof caches !== "undefined" && isSecureContext ? caches.open(NAMES[bucket]).catch(() => null) : Promise.resolve(null);
    opened.set(bucket, p);
  }
  return p;
}

/** 缓存 key 统一成绝对地址（Cache API 只接受 http(s) 请求）。 */
const keyOf = (key: string) => new URL(key, location.href).href;

/** 去掉查询串（预签名参数）：对象 key 在路径里，同一对象换了签名也是同一个缓存项。 */
export const withoutQuery = (url: string) => url.split("?")[0];

/** HTTP 错误（带状态码，调用方据此决定是否换地址重试）。 */
export class AssetHttpError extends Error {
  constructor(
    readonly status: number,
    url: string,
  ) {
    super(`资源加载失败（HTTP ${status}）：${withoutQuery(url)}`);
  }
}

export interface FetchAssetOptions {
  bucket?: CacheBucket;
  /** 缓存 key，默认为地址本身；地址带一次性签名时传不带签名的稳定 key。 */
  key?: string;
  /** 下载进度 0–1（本机已有时直接回调 1）。 */
  onProgress?: (f: number) => void;
  /** 预期大小（字节），服务器没给长度或经过压缩时用于计算进度。 */
  bytes?: number;
}

const inflight = new Map<string, Promise<Blob>>();

/** 取文件：本机缓存优先，否则下载并存入缓存。同一文件同时只下载一次。 */
export function fetchAsset(url: string, opts: FetchAssetOptions = {}): Promise<Blob> {
  const bucket = opts.bucket ?? "static";
  const key = keyOf(opts.key ?? url);
  const id = `${bucket} ${key}`;
  const running = inflight.get(id);
  if (running) {
    // 合并到进行中的下载；进度只回调最终完成
    return running.then((b) => (opts.onProgress?.(1), b));
  }
  const p = (async () => {
    const cache = await open(bucket);
    const net = devNet();
    if (cache && !net?.cold) {
      const hit = await cache.match(key).catch(() => undefined);
      if (hit) {
        opts.onProgress?.(1);
        return hit.blob();
      }
    }
    const res = await fetch(url);
    if (!res.ok) throw new AssetHttpError(res.status, url);
    const blob = await readBody(res, opts, net);
    if (cache) void store(cache, bucket, key, blob);
    return blob;
  })();
  inflight.set(id, p);
  void p.then(
    () => inflight.delete(id),
    () => inflight.delete(id),
  );
  return p;
}

/** 本机是否已有（预热前跳过已缓存的文件）。 */
export async function hasAsset(key: string, bucket: CacheBucket = "static"): Promise<boolean> {
  const cache = await open(bucket);
  return !!cache && !!(await cache.match(keyOf(key)).catch(() => undefined));
}

async function store(cache: Cache, bucket: CacheBucket, key: string, blob: Blob) {
  try {
    await cache.put(key, new Response(blob, { headers: { "Content-Type": blob.type || "application/octet-stream" } }));
    if (bucket === "replay") await trimOldest(cache, REPLAY_MAX);
  } catch {
    // 空间不足等：不缓存，下次重新下载
  }
}

/** 超出数量时删最早存入的（keys() 按存入顺序返回）。 */
async function trimOldest(cache: Cache, max: number) {
  const keys = await cache.keys();
  for (const k of keys.slice(0, Math.max(0, keys.length - max))) await cache.delete(k);
}

async function readBody(res: Response, opts: FetchAssetOptions, net: DevNet | null): Promise<Blob> {
  if (!opts.onProgress && !net) return res.blob();
  const type = res.headers.get("Content-Type") ?? "";
  // 经过压缩时 Content-Length 是压缩后的长度，读到的是解压后的字节，不能直接相除
  const len = res.headers.get("Content-Encoding") ? 0 : Number(res.headers.get("Content-Length") ?? 0);
  const total = opts.bytes || len;
  const reader = res.body?.getReader();
  if (!reader) return res.blob();
  const parts: Uint8Array<ArrayBuffer>[] = [];
  let loaded = 0;
  const t0 = performance.now();
  if (net) await sleep(net.latency);
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value);
    loaded += value.byteLength;
    if (net) {
      // 按设定速率限速：已读字节对应的时间还没到就等
      const due = net.latency + (loaded / (net.kbps * 1024)) * 1000 - (performance.now() - t0);
      if (due > 0) await sleep(due);
    }
    if (total > 0) opts.onProgress?.(Math.min(loaded / total, 0.99));
  }
  opts.onProgress?.(1);
  return new Blob(parts, { type });
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// ---- 版本清理 ----

/**
 * 删除 static 缓存里同类资源的旧版本：地址形如 …/<segment>/<version>/…，version 不等于 current 的删掉。
 * 新版本上线后旧文件不会再被请求，不清理会一直占空间。
 */
export async function pruneVersions(segment: string, current: string) {
  const cache = await open("static");
  if (!cache) return;
  const re = new RegExp(`/${segment}/([0-9a-f]{8,})/`);
  try {
    for (const req of await cache.keys()) {
      const m = re.exec(new URL(req.url).pathname);
      if (m && m[1] !== current) await cache.delete(req);
    }
  } catch {
    // 忽略
  }
}

const pruned = new Set<string>();
/** 按资源地址前缀（…/<segment>/<version>，如 weapons3d 的 base_url）清理旧版本；每个页面会话里只做一次。 */
export function pruneOldVersions(baseUrl: string) {
  const m = /\/([a-z0-9]+)\/([0-9a-f]{8,})\/?$/.exec(new URL(baseUrl, location.href).pathname);
  if (!m || pruned.has(m[0])) return;
  pruned.add(m[0]);
  whenIdle(() => void pruneVersions(m[1], m[2]));
}

/** 清空回合数据（退出登录时：战队 demo 不留在这台电脑上）。 */
export async function clearReplayCache() {
  opened.delete("replay");
  if (typeof caches !== "undefined") await caches.delete(NAMES.replay).catch(() => false);
}

// ---- 后台预热 ----

type Job = () => Promise<unknown>;
const queue: Job[] = [];
let active = 0;
const CONCURRENCY = 2;

function whenIdle(fn: () => void) {
  if (typeof requestIdleCallback === "function") requestIdleCallback(() => fn(), { timeout: 3000 });
  else setTimeout(fn, 200);
}

/** 省流量模式 / 2G 网络下不做预热（只加载正在看的内容）。 */
function warmAllowed(): boolean {
  const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  return !(c?.saveData || c?.effectiveType === "2g" || c?.effectiveType === "slow-2g");
}

function pump() {
  while (active < CONCURRENCY && queue.length) {
    const job = queue.shift()!;
    active++;
    whenIdle(() => {
      void job()
        .catch(() => undefined)
        .finally(() => {
          active--;
          pump();
        });
    });
  }
}

/** 空闲时在后台执行（排队、限制并发、忽略错误）。 */
export function warmTask(job: Job) {
  if (!warmAllowed()) return;
  queue.push(job);
  pump();
}

/** 空闲时把文件下载进本机缓存（已缓存的跳过）。 */
export function warmAsset(url: string, opts: Omit<FetchAssetOptions, "onProgress"> = {}) {
  warmTask(async () => {
    if (await hasAsset(opts.key ?? url, opts.bucket)) return;
    await fetchAsset(url, opts);
  });
}

// ---- 开发用慢网模拟 ----
// 本地开发时文件从本机磁盘读，快得看不到加载状态。在浏览器控制台设置后刷新页面即可模拟：
//   localStorage.setItem("cs2coach.dev.net", "300")        // 限速 300 KB/s（另加 150 ms 延迟）
//   localStorage.setItem("cs2coach.dev.net", "300,cold")   // 同上，并且不读本机缓存（模拟首次访问）
//   localStorage.removeItem("cs2coach.dev.net")            // 恢复
// 只对经过 fetchAsset 的文件生效（3D 场景 / 模型 / 皮肤 3D / 回合数据），正式构建里不存在。

interface DevNet {
  kbps: number;
  latency: number;
  cold: boolean;
}

function devNet(): DevNet | null {
  if (!import.meta.env.DEV) return null;
  try {
    const raw = localStorage.getItem("cs2coach.dev.net");
    if (!raw) return null;
    const [speed, ...flags] = raw.split(",").map((s) => s.trim());
    const kbps = Number(speed);
    if (!(kbps > 0)) return null;
    return { kbps, latency: 150, cold: flags.includes("cold") };
  } catch {
    return null;
  }
}
